import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GridFour, ListDashes, MagnifyingGlass, Heart, ChatCircle, ShieldCheck, MapPin, FunnelSimple, FloppyDisk, X, BookmarkSimple, EnvelopeSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import StoriesBar from '../components/StoriesBar';
import { useLang } from '../contexts/LanguageContext';
import { SILHOUETTE_AVATAR, getAvatarUrl } from '../lib/avatar';
import { WORLD_COUNTRIES } from '../lib/countries';
import { track } from '../lib/analytics';
import api from '../lib/api';

const DEFAULT_FILTERS = {
  country: 'all',
  currentCountry: 'all',
  city: '',
  gender: 'both',
  ageRange: [18, 60],
  verifiedOnly: false,
};

const COUNTRIES_MAP = {
  slovenia: 'Slovenia', croatia: 'Croatia', bosnia: 'Bosnia & Herzegovina',
  serbia: 'Serbia', montenegro: 'Montenegro', north_macedonia: 'North Macedonia', kosovo: 'Kosovo'
};

function UserCard({ user, viewMode, onMessage, onLike, liked }) {
  const navigate = useNavigate();
  const photo = getAvatarUrl(user);
  const age = user.dob ? Math.floor((Date.now() - new Date(user.dob).getTime()) / 31557600000) : null;

  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="profile-card flex items-center gap-4 p-4 cursor-pointer"
        onClick={() => navigate(`/profile/${user.id}`)}
        data-testid={`user-card-${user.id}`}
      >
        <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
          <img src={photo} alt={user.first_name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold truncate">{user.first_name}, {age || '?'}</span>
            {user.is_verified && <span className="verified-badge">Verified</span>}
            {user.is_premium && <ShieldCheck size={16} weight="fill" className="text-[#D4AF37]" />}
          </div>
          <div className="flex items-center gap-1 text-xs text-[#9EABCE] mt-1">
            <MapPin size={12} weight="light" />
            {user.current_city || COUNTRIES_MAP[user.heritage_country] || 'Unknown'}
          </div>
        </div>
        <div className="flex gap-2">
          <motion.button
            onClick={(e) => { e.stopPropagation(); onLike(user.id); }}
            whileTap={{ scale: 0.88 }}
            animate={liked ? { scale: [1, 1.25, 1] } : { scale: 1 }}
            transition={{ duration: 0.45 }}
            className={`p-2 rounded-xl transition-colors duration-500 ${
              liked
                ? 'bg-red-500/15 text-red-400 hover:bg-red-500/25'
                : 'bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 animate-heartbeat'
            }`}
            data-testid={`like-btn-${user.id}`}
            aria-pressed={liked}
          >
            <Heart size={20} weight="fill" />
          </motion.button>
          <button onClick={(e) => { e.stopPropagation(); onMessage(user.id); }} className="p-2 rounded-xl bg-gradient-to-br from-[#D4AF37]/15 to-[#D4AF37]/5 text-white border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 hover:from-[#D4AF37]/25 hover:to-[#D4AF37]/10 transition-all" data-testid={`message-btn-${user.id}`}>
            <ChatCircle size={20} weight="fill" />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="profile-card cursor-pointer group"
      onClick={() => navigate(`/profile/${user.id}`)}
      data-testid={`user-card-${user.id}`}
    >
      <div className="relative aspect-[3/4] overflow-hidden rounded-t-2xl">
        <img src={photo} alt={user.first_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-lg">{user.first_name}, {age || '?'}</span>
            {user.is_verified && <span className="verified-badge">Verified</span>}
          </div>
          <div className="flex items-center gap-1 text-xs text-white/70 mt-1">
            <MapPin size={12} weight="light" />
            {user.current_city || COUNTRIES_MAP[user.heritage_country] || 'Unknown'}
          </div>
        </div>
      </div>
      <div className="p-3 flex gap-2">
        <motion.button
          onClick={(e) => { e.stopPropagation(); onLike(user.id); }}
          whileTap={{ scale: 0.94 }}
          animate={liked ? { scale: [1, 1.08, 1] } : { scale: 1 }}
          transition={{ duration: 0.45 }}
          className={`relative flex-1 py-2 rounded-xl font-semibold text-sm transition-colors duration-500 flex items-center justify-center gap-1.5 overflow-hidden ${
            liked
              ? 'bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30'
              : 'bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 border border-transparent'
          }`}
          data-testid={`like-btn-${user.id}`}
          aria-pressed={liked}
        >
          <Heart size={16} weight="fill" className={liked ? '' : 'animate-heartbeat'} />
          <span>{liked ? 'Blinked' : 'Blink'}</span>
          {liked && (
            <motion.span
              initial={{ scale: 0, opacity: 1 }}
              animate={{ scale: 2.4, opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="absolute inset-0 rounded-xl border-2 border-red-400/60 pointer-events-none"
            />
          )}
        </motion.button>
        <motion.button
          onClick={(e) => { e.stopPropagation(); onMessage(user.id); }}
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.96 }}
          className="group/chat relative flex-1 py-2 rounded-xl font-bold text-sm text-white border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/20 via-[#D4AF37]/10 to-transparent hover:border-[#D4AF37]/70 hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all flex items-center justify-center gap-1.5 overflow-hidden"
          data-testid={`message-btn-${user.id}`}
        >
          <span className="absolute inset-0 -translate-x-full group-hover/chat:translate-x-full bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent transition-transform duration-700 pointer-events-none" />
          <ChatCircle size={16} weight="fill" className="text-[#D4AF37] group-hover/chat:scale-110 transition-transform" />
          <span className="relative">Chat</span>
        </motion.button>
      </div>
    </motion.div>
  );
}

export default function BrowsePage() {
  const { user: currentUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [country, setCountry] = useState('all');
  const [currentCountry, setCurrentCountry] = useState('all');
  const [city, setCity] = useState('');
  const [gender, setGender] = useState('both');
  const [ageRange, setAgeRange] = useState([18, 60]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [likedIds, setLikedIds] = useState(new Set());
  const [browsePage, setBrowsePage] = useState(1);
  const [browsePages, setBrowsePages] = useState(1);
  const [browseTotal, setBrowseTotal] = useState(0);
  const BROWSE_PER_PAGE = 24;

  // `appliedFilters` is what's actually sent to /api/users and /api/stories.
  // The individual state (country, currentCountry, ...) above is the pending, user-editable form.
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const f = appliedFilters;
      const params = { page, limit: BROWSE_PER_PAGE };
      if (f.country && f.country !== 'all') params.country = f.country;
      if (f.currentCountry && f.currentCountry !== 'all') params.current_country = f.currentCountry;
      if (f.city) params.city = f.city;
      if (f.gender && f.gender !== 'both') params.gender = f.gender;
      if (f.ageRange[0] !== 18) params.min_age = f.ageRange[0];
      if (f.ageRange[1] !== 60) params.max_age = f.ageRange[1];
      if (f.verifiedOnly) params.verified_only = true;
      const { data } = await api.get('/users', { params });
      setUsers(data.users || []);
      setBrowsePage(data.page || page);
      setBrowsePages(data.pages || 1);
      setBrowseTotal(data.total || 0);
      // Scroll to top when paginating
      if (page > 1) window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Failed to fetch users', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => { fetchUsers(1); }, [fetchUsers]);

  const applyFilters = () => {
    const snapshot = {
      country, currentCountry, city, gender, ageRange, verifiedOnly,
    };
    setAppliedFilters(snapshot);
    setShowFilters(false);
    track('search_applied', {
      has_country: snapshot.country !== 'all',
      has_current_country: snapshot.currentCountry !== 'all',
      has_city: !!snapshot.city,
      gender: snapshot.gender,
      age_min: snapshot.ageRange[0],
      age_max: snapshot.ageRange[1],
      verified_only: snapshot.verifiedOnly,
    });
  };

  const resetFilters = () => {
    setCountry('all');
    setCurrentCountry('all');
    setCity('');
    setGender('both');
    setAgeRange([18, 60]);
    setVerifiedOnly(false);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const fetchSavedSearches = useCallback(async () => {
    if (!currentUser) return;
    try {
      const { data } = await api.get('/searches');
      setSavedSearches(data.searches || []);
    } catch {}
  }, [currentUser]);

  useEffect(() => { fetchSavedSearches(); }, [fetchSavedSearches]);

  // Load my previously-sent Blinks so UI reflects the red state
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const { data } = await api.get('/users/me/likes-sent');
        setLikedIds(new Set(data.to_user_ids || []));
      } catch {}
    })();
  }, [currentUser]);

  const handleSaveSearch = async () => {
    const name = searchName.trim();
    if (!name) return;
    const filters = { country, currentCountry, city, gender, ageRange, verifiedOnly };
    try {
      const { data } = await api.post('/searches', { name, filters });
      setSavedSearches([data.search, ...savedSearches]);
      setShowSaveDialog(false);
      setSearchName('');
    } catch (err) {
      console.error('Save search failed', err);
    }
  };

  const applySavedSearch = (s) => {
    const f = s.filters || {};
    const nextCountry = f.country || 'all';
    const nextCurrentCountry = f.currentCountry || 'all';
    const nextCity = f.city || '';
    const nextGender = f.gender || 'both';
    const nextAge = Array.isArray(f.ageRange) && f.ageRange.length === 2 ? f.ageRange : [18, 60];
    const nextVerified = !!f.verifiedOnly;
    setCountry(nextCountry);
    setCurrentCountry(nextCurrentCountry);
    setCity(nextCity);
    setGender(nextGender);
    setAgeRange(nextAge);
    setVerifiedOnly(nextVerified);
    // Immediately apply (no Search-button click needed)
    setAppliedFilters({
      country: nextCountry,
      currentCountry: nextCurrentCountry,
      city: nextCity,
      gender: nextGender,
      ageRange: nextAge,
      verifiedOnly: nextVerified,
    });
    setShowFilters(false);
  };

  const deleteSavedSearch = async (id) => {
    try {
      await api.delete(`/searches/${id}`);
      setSavedSearches(savedSearches.filter(s => s.id !== id));
    } catch {}
  };

  const toggleAlerts = async (s) => {
    try {
      const { data } = await api.patch(`/searches/${s.id}`, { email_alerts: !s.email_alerts });
      setSavedSearches(savedSearches.map(x => x.id === s.id ? data.search : x));
    } catch {}
  };

  const handleMessage = (userId) => {
    if (!currentUser) { navigate('/auth'); return; }
    track('chat_opened', { source: 'browse', target_user_id: userId });
    navigate(`/chat?to=${userId}`);
  };

  const handleLike = async (userId) => {
    if (!currentUser) { navigate('/auth'); return; }
    // Optimistic UI: flash red immediately
    setLikedIds(prev => new Set(prev).add(userId));
    track('blink_sent', { target_user_id: userId });
    try {
      await api.post(`/users/${userId}/like`);
    } catch {
      // Roll back on failure
      setLikedIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="browse-page">
      <div className="max-w-6xl mx-auto">
        {/* No-photo prompt for current user */}
        {currentUser && (!currentUser.photos || currentUser.photos.length === 0) && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="upload-photo-banner"
            className="glass-card mb-6 p-4 flex items-center gap-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5"
          >
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
              <img src="data:image/svg+xml;utf8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 viewBox=%220 0 24 24%22 stroke=%22%23D4AF37%22 stroke-width=%221.5%22%3E%3Cpath stroke-linecap=%22round%22 stroke-linejoin=%22round%22 d=%22M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z%22/%3E%3Cpath stroke-linecap=%22round%22 stroke-linejoin=%22round%22 d=%22M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z%22/%3E%3C/svg%3E" alt="" className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold">Add your first photo to be seen</p>
              <p className="text-[#9EABCE] text-xs mt-0.5">Your profile is hidden from Browse until you upload at least one photo.</p>
            </div>
            <button
              onClick={() => navigate('/profile/edit')}
              data-testid="banner-upload-btn"
              className="btn-gold px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap"
            >
              Upload Photo
            </button>
          </motion.div>
        )}

        {/* Stories Bar */}
        <StoriesBar filters={appliedFilters} />

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-medium tracking-tight text-white font-['Outfit']">
            {t('discover')}
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
              className={`p-2.5 rounded-xl transition-all ${showFilters ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-white/5 text-[#9EABCE] hover:text-white'}`}
            >
              <FunnelSimple size={20} weight="light" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              data-testid="view-grid-btn"
              className={`p-2.5 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-white/5 text-[#9EABCE] hover:text-white'}`}
            >
              <GridFour size={20} weight={viewMode === 'grid' ? 'fill' : 'light'} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              data-testid="view-list-btn"
              className={`p-2.5 rounded-xl transition-all ${viewMode === 'list' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-white/5 text-[#9EABCE] hover:text-white'}`}
            >
              <ListDashes size={20} weight={viewMode === 'list' ? 'fill' : 'light'} />
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="glass-card p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-2 block">Origin Country</label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger data-testid="filter-country" className="bg-white/5 border-white/10 text-white rounded-xl">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B112B] border-white/10">
                    <SelectItem value="all" className="text-white">All Countries</SelectItem>
                    {Object.entries(COUNTRIES_MAP).map(([key, name]) => (
                      <SelectItem key={key} value={key} className="text-white">{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-2 block">Current Country</label>
                <Select value={currentCountry} onValueChange={setCurrentCountry}>
                  <SelectTrigger data-testid="filter-current-country" className="bg-white/5 border-white/10 text-white rounded-xl">
                    <SelectValue placeholder="Anywhere" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B112B] border-white/10 max-h-[280px]">
                    <SelectItem value="all" className="text-white">Anywhere</SelectItem>
                    {WORLD_COUNTRIES.map(c => (
                      <SelectItem key={c.code} value={c.code} className="text-white">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-2 block">City</label>
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9EABCE]" />
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Search by city..."
                    data-testid="filter-city"
                    className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-2 block">Gender</label>
                <div className="flex gap-1 bg-white/5 rounded-xl p-1" data-testid="filter-gender">
                  {[
                    { id: 'both', label: 'Both' },
                    { id: 'man', label: 'Male' },
                    { id: 'woman', label: 'Female' }
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setGender(opt.id)}
                      data-testid={`filter-gender-${opt.id}`}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                        gender === opt.id ? 'bg-[#D4AF37] text-[#060A1A]' : 'text-[#9EABCE] hover:text-white'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-2 block">
                  Age <span className="text-[#9EABCE] font-normal normal-case tracking-normal ml-1">{ageRange[0]} – {ageRange[1]}</span>
                </label>
                <div className="pt-4 px-1" data-testid="filter-age">
                  <Slider
                    min={18}
                    max={80}
                    step={1}
                    value={ageRange}
                    onValueChange={(val) => setAgeRange(val)}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-2 block">Verified Only</label>
                <div className="flex items-center gap-3 mt-2">
                  <Switch
                    checked={verifiedOnly}
                    onCheckedChange={setVerifiedOnly}
                    data-testid="filter-verified"
                    className="data-[state=checked]:bg-[#D4AF37]"
                  />
                  <span className="text-sm text-[#9EABCE]">{verifiedOnly ? 'Showing verified' : 'Show all'}</span>
                </div>
              </div>
            </div>

            {/* Search / Reset Actions */}
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-end gap-2">
              <button
                onClick={resetFilters}
                data-testid="reset-filters-btn"
                className="px-4 py-2 rounded-lg text-xs font-semibold text-[#9EABCE] hover:text-white hover:bg-white/5 transition-all"
              >
                Reset
              </button>
              <button
                onClick={applyFilters}
                data-testid="apply-search-btn"
                className="btn-gold px-5 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-2"
              >
                <MagnifyingGlass size={16} weight="bold" /> Search
              </button>
            </div>

            {/* Saved Searches Row */}
            {currentUser && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] flex items-center gap-1.5">
                      <BookmarkSimple size={14} weight="fill" /> Saved
                    </span>
                    {savedSearches.length === 0 && (
                      <span className="text-xs text-[#9EABCE]/60">No saved searches yet</span>
                    )}
                    {savedSearches.map(s => (
                      <div
                        key={s.id}
                        data-testid={`saved-search-${s.id}`}
                        className="group inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5 transition-all"
                      >
                        <button
                          onClick={() => applySavedSearch(s)}
                          data-testid={`apply-search-${s.id}`}
                          className="font-medium hover:text-[#D4AF37]"
                        >
                          {s.name}
                        </button>
                        <button
                          onClick={() => toggleAlerts(s)}
                          data-testid={`toggle-alert-${s.id}`}
                          title={s.email_alerts ? 'Weekly email alerts: ON — click to turn off' : 'Turn on weekly email alerts'}
                          className={`p-1 rounded-full transition-all ${
                            s.email_alerts
                              ? 'text-[#D4AF37] bg-[#D4AF37]/15 hover:bg-[#D4AF37]/25'
                              : 'text-[#9EABCE] hover:text-[#D4AF37] hover:bg-white/5'
                          }`}
                          aria-label="Toggle email alerts"
                        >
                          <EnvelopeSimple size={12} weight={s.email_alerts ? 'fill' : 'regular'} />
                        </button>
                        <button
                          onClick={() => deleteSavedSearch(s.id)}
                          data-testid={`delete-search-${s.id}`}
                          className="p-0.5 rounded-full text-[#9EABCE] hover:text-red-400 hover:bg-red-400/10 transition-all"
                          aria-label="Delete saved search"
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {!showSaveDialog ? (
                    <button
                      onClick={() => setShowSaveDialog(true)}
                      data-testid="save-search-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 text-xs font-bold hover:bg-[#D4AF37]/20 transition-all whitespace-nowrap"
                    >
                      <FloppyDisk size={14} weight="fill" /> Save Search
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        autoFocus
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSearch(); if (e.key === 'Escape') { setShowSaveDialog(false); setSearchName(''); } }}
                        placeholder="e.g. Croatia 25–30"
                        maxLength={60}
                        data-testid="save-search-name"
                        className="w-44 h-8 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 rounded-lg text-xs"
                      />
                      <button
                        onClick={handleSaveSearch}
                        disabled={!searchName.trim()}
                        data-testid="save-search-confirm"
                        className="px-3 py-1.5 rounded-lg btn-gold text-xs font-bold disabled:opacity-40"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setShowSaveDialog(false); setSearchName(''); }}
                        data-testid="save-search-cancel"
                        className="p-1.5 rounded-lg text-[#9EABCE] hover:text-white hover:bg-white/5 transition-all"
                        aria-label="Cancel"
                      >
                        <X size={14} weight="bold" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Users Grid/List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#9EABCE] text-lg">No users found</p>
            <p className="text-[#9EABCE]/60 text-sm mt-2">Try adjusting your filters</p>
          </div>
        ) : (
          <div className={viewMode === 'grid'
            ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'
            : 'space-y-3'
          }>
            {users.map(user => (
              <UserCard
                key={user.id}
                user={user}
                viewMode={viewMode}
                onMessage={handleMessage}
                onLike={handleLike}
                liked={likedIds.has(user.id)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && users.length > 0 && browsePages > 1 && (
          <div className="mt-6 flex items-center justify-between gap-2 flex-wrap" data-testid="browse-pagination">
            <p className="text-xs text-[#9EABCE]/70">
              Showing <span className="text-white font-semibold">{(browsePage - 1) * BROWSE_PER_PAGE + 1}</span>–<span className="text-white font-semibold">{Math.min(browsePage * BROWSE_PER_PAGE, browseTotal)}</span> of <span className="text-white font-semibold">{browseTotal}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchUsers(Math.max(1, browsePage - 1))}
                disabled={browsePage <= 1}
                data-testid="browse-prev"
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-white/5 text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: browsePages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === browsePages || Math.abs(p - browsePage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === '...' ? (
                    <span key={`gap-${i}`} className="px-2 text-[#9EABCE]/50 text-xs">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => fetchUsers(p)}
                      data-testid={`browse-page-${p}`}
                      className={`min-w-[32px] h-8 rounded-lg text-xs font-semibold transition-all ${
                        p === browsePage ? 'bg-[#D4AF37] text-[#060A1A]' : 'bg-white/5 text-[#9EABCE] hover:bg-white/10'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
              </div>
              <button
                onClick={() => fetchUsers(Math.min(browsePages, browsePage + 1))}
                disabled={browsePage >= browsePages}
                data-testid="browse-next"
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-white/5 text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
